import * as React from 'react';
import { Input } from '../Input';
import {
	buildSlotString,
	countMaskTokens,
	extractRawValue,
	firstEmptyTokenIndex,
	firstTokenIndexAtOrAfter,
	isMaskToken,
	matchesToken,
	nextTokenIndex,
	padPastedSegments,
	padSegmentBeforeSeparator,
	prevFilledTokenIndex,
	slotStringToValue,
} from './mask';

export { MASK_PRESETS } from './presets';

export interface IMaskedInputValueChange {
	/** Token characters only, e.g. `10102026`. */
	raw: string;
	/** Masked string, e.g. `10/10/2026`, or `''` when no slot is filled. */
	formatted: string;
	/** True when every token position in the mask is filled. */
	isComplete: boolean;
	/** `isComplete && (validate?.(formatted) ?? true)`. */
	isValid: boolean;
}

type IInputProps = React.ComponentProps<typeof Input>;

type IProps = Omit<IInputProps, 'value' | 'onChange'> & {
	/** Mask pattern: `9` = digit, `a` = letter, `*` = alphanumeric; other characters are literals. */
	mask: string;
	/** Controlled formatted value; store `onValueChange`'s `formatted` back into this. */
	value: string;
	onValueChange: (change: IMaskedInputValueChange) => void;
	/** Optional semantic validation, only consulted once the mask is complete. */
	validate?: (formatted: string) => boolean;
	/**
	 * Character used to represent an empty (unfilled) token slot.
	 * Defaults to `'_'`. Must be a single character that is not a valid token value for any slot.
	 */
	placeholderChar?: string;
	/**
	 * Separator characters that, when typed inside a partially-filled all-digit segment,
	 * zero-pad it and jump to the next segment — e.g. with `['/', '-', '.']` on a
	 * `99/99/9999` mask, typing `1/2/2000` normalizes to `01/02/2000`. Defaults to none.
	 */
	separators?: readonly string[];
	/**
	 * Optional transform applied to incoming text BEFORE the mask extracts characters.
	 * Use to normalize input the fixed-length mask would otherwise truncate — e.g. drop a
	 * country code so an 11-digit value fills a 10-digit mask. Applies to both the paste
	 * path and the onChange autofill/dictation fallback (so an autofilled `+1…` E.164
	 * number is normalized the same way a paste would be).
	 */
	normalizePaste?: (pastedText: string) => string;
	/**
	 * When set, a value that begins with this prefix (e.g. `'+'`) switches the field into
	 * passthrough mode: the mask is bypassed and the raw text is kept verbatim. Use for inputs
	 * that are normally a fixed national format but must also accept free-form values the mask
	 * can't express — e.g. a US phone mask that should still allow `+<countrycode>` international
	 * entry. Mode is derived from the current value, so deleting the prefix back out reverts to
	 * the mask. Entry is triggered by typing the prefix into an empty field or pasting a value
	 * that starts with it; in passthrough `onValueChange` reports the raw text as both `raw` and
	 * `formatted` and leaves validation to the consumer.
	 */
	passthroughPrefix?: string;
};

const DEFAULT_PLACEHOLDER_CHAR = '_';

/** Format-as-you-type masked text input with overwrite semantics and caret preservation. See README.md for usage and presets. */
export const MaskedInput = React.forwardRef<React.ElementRef<'input'>, IProps>(function MaskedInput(
	{
		mask,
		value,
		onValueChange,
		validate,
		placeholderChar = DEFAULT_PLACEHOLDER_CHAR,
		separators,
		normalizePaste,
		passthroughPrefix,
		...restProps
	},
	forwardedRef
) {
	const inputRef = React.useRef<HTMLInputElement | null>(null);
	const pendingCaretRef = React.useRef<number | null>(null);

	// Merge forwarded ref with our internal ref
	const mergedRef = React.useCallback(
		(node: HTMLInputElement | null) => {
			inputRef.current = node;
			if (typeof forwardedRef === 'function') {
				forwardedRef(node);
			} else if (forwardedRef != null) {
				forwardedRef.current = node;
			}
		},
		[forwardedRef]
	);

	// Dev-time guard: a placeholderChar that matches a token in the mask makes complete
	// values round-trip to '' (reported empty) — a silent data-loss footgun. Warn once.
	const hasWarnedRef = React.useRef(false);
	React.useEffect(() => {
		if (hasWarnedRef.current) {
			return;
		}
		if (mask.split('').some(maskChar => matchesToken(placeholderChar, maskChar))) {
			hasWarnedRef.current = true;
			// eslint-disable-next-line no-console
			console.warn(
				`MaskedInput: placeholderChar "${placeholderChar}" matches a token in mask "${mask}" — ` +
					'complete values will be reported empty. Choose a non-token placeholder character.'
			);
		}
	}, [mask, placeholderChar]);

	// After controlled re-render, restore the caret to the pending position
	React.useLayoutEffect(() => {
		if (pendingCaretRef.current != null && inputRef.current != null) {
			const pos = pendingCaretRef.current;
			pendingCaretRef.current = null;
			inputRef.current.setSelectionRange(pos, pos);
		}
	});

	/** Compute the structured change payload from a slot string. */
	const buildChange = React.useCallback(
		(slotString: string): IMaskedInputValueChange => {
			const formatted = slotStringToValue(slotString, mask, placeholderChar);
			const raw = extractRawValue(slotString, mask);
			const isComplete = raw.length === countMaskTokens(mask);
			const isValid = isComplete && (validate?.(formatted) ?? true);
			return { raw, formatted, isComplete, isValid };
		},
		[mask, placeholderChar, validate]
	);

	/** Emit a change and schedule a caret restore. */
	const emit = React.useCallback(
		(slotString: string, caret: number) => {
			pendingCaretRef.current = caret;
			onValueChange(buildChange(slotString));
		},
		[buildChange, onValueChange]
	);

	// Passthrough mode is derived from the value: once it starts with the prefix the mask is
	// bypassed, and deleting the prefix back out reverts to masking with no extra state.
	const isPassthrough = !!passthroughPrefix && value.startsWith(passthroughPrefix);

	/** Emit a raw (unmasked) passthrough value verbatim and schedule a caret restore. */
	const emitPassthrough = React.useCallback(
		(raw: string, caret: number) => {
			pendingCaretRef.current = caret;
			// Validation is the consumer's job in passthrough; report the raw text as-is.
			onValueChange({ raw, formatted: raw, isComplete: raw.length > 0, isValid: raw.length > 0 });
		},
		[onValueChange]
	);

	/** Handle keydown-driven edits for overwrite semantics. */
	const handleKeyDown = React.useCallback(
		(e: React.KeyboardEvent<HTMLInputElement>) => {
			// Let the consumer's onKeyDown run first (e.g. Enter handler in UpdateDateForm)
			restProps.onKeyDown?.(e);
			if (e.isDefaultPrevented()) {
				return;
			}

			const input = inputRef.current;
			if (input == null) {
				return;
			}

			const selStart = input.selectionStart ?? 0;
			const selEnd = input.selectionEnd ?? 0;
			const hasRange = selStart !== selEnd;

			// Get the current displayed slot string (normalized to mask.length)
			const currentSlot = buildSlotString(value, mask, placeholderChar);
			const slots = currentSlot.split('');

			/** Clear all token slots in [start, end) to placeholder. */
			const clearRange = (start: number, end: number) => {
				for (let i = start; i < Math.min(end, mask.length); i++) {
					if (isMaskToken(mask[i])) {
						slots[i] = placeholderChar;
					}
				}
			};

			// ── Printable character (no Ctrl/Cmd/Alt meta) ───────────────────────
			if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				e.preventDefault();

				// Typing the passthrough prefix into an empty field hands control to free-text mode.
				// (The mask would otherwise reject the prefix as a non-token char.)
				if (passthroughPrefix && e.key === passthroughPrefix && extractRawValue(currentSlot, mask) === '') {
					emitPassthrough(passthroughPrefix, passthroughPrefix.length);
					return;
				}

				let target: number;
				if (hasRange) {
					// Clear tokens inside the range first
					clearRange(selStart, selEnd);
					target = firstTokenIndexAtOrAfter(mask, selStart);
					if (target === -1 || target >= selEnd) {
						// No token in range — just clear and emit with caret at start
						emit(slots.join(''), selStart);
						return;
					}
				} else {
					target = firstTokenIndexAtOrAfter(mask, selStart);
					if (target === -1) {
						// Caret sits past the last token, but the field may still have earlier gaps
						// (focus landed at the end of a partially-filled value) — fill the first empty
						// slot rather than swallowing the keystroke.
						target = firstEmptyTokenIndex(mask, slots, placeholderChar);
					}
				}

				if (target === -1) {
					// Field is full; nothing to fill.
					return;
				}

				const maskChar = mask[target];
				if (maskChar === undefined || !matchesToken(e.key, maskChar)) {
					// A separator typed inside a partial digit segment commits it zero-padded and
					// jumps to the next segment (e.g. `1/2/2000` → `01/02/2000`). Only on a collapsed
					// caret — range edits have already cleared their slots above.
					if (!hasRange && separators?.includes(e.key)) {
						const padded = padSegmentBeforeSeparator(slots, mask, selStart, placeholderChar);
						if (padded) {
							emit(padded.slots.join(''), padded.caret);
						}
						return;
					}
					// Character doesn't match the slot type — no change. preventDefault (already
					// called above) holds the caret; setting pendingCaretRef without an emit would
					// leave a stale value to be applied on the next unrelated re-render.
					return;
				}

				slots[target] = e.key;
				const newCaret = nextTokenIndex(mask, target);
				emit(slots.join(''), newCaret);
				return;
			}

			// ── Clear-all: cmd/ctrl + Backspace|Delete wipes the whole field ───────
			// On a short single-line masked field, "delete to line start" / "delete word"
			// pragmatically means "clear the field".
			if ((e.key === 'Backspace' || e.key === 'Delete') && (e.metaKey || e.ctrlKey)) {
				e.preventDefault();
				clearRange(0, mask.length);
				const firstToken = firstTokenIndexAtOrAfter(mask, 0);
				emit(slots.join(''), firstToken === -1 ? 0 : firstToken);
				return;
			}

			// ── Backspace ─────────────────────────────────────────────────────────
			if (e.key === 'Backspace') {
				e.preventDefault();
				if (hasRange) {
					clearRange(selStart, selEnd);
					emit(slots.join(''), selStart);
				} else {
					const t = prevFilledTokenIndex(mask, slots, selStart, placeholderChar);
					if (t === -1) {
						return;
					}
					slots[t] = placeholderChar;
					emit(slots.join(''), t);
				}
				return;
			}

			// ── Delete ────────────────────────────────────────────────────────────
			if (e.key === 'Delete') {
				e.preventDefault();
				if (hasRange) {
					clearRange(selStart, selEnd);
					emit(slots.join(''), selStart);
				} else {
					const t = firstTokenIndexAtOrAfter(mask, selStart);
					if (t === -1) {
						return;
					}
					slots[t] = placeholderChar;
					emit(slots.join(''), t);
				}
				return;
			}

			// All other keys (arrows, Tab, Home/End, Ctrl+A, etc.) pass through untouched
		},
		[value, mask, placeholderChar, separators, emit, emitPassthrough, passthroughPrefix, restProps]
	);

	/** Handle paste events. */
	const handlePaste = React.useCallback(
		(e: React.ClipboardEvent<HTMLInputElement>) => {
			e.preventDefault();
			const input = inputRef.current;
			if (input == null) {
				return;
			}

			const rawPaste = e.clipboardData.getData('text');

			// A paste that begins with the passthrough prefix hands control to free-text mode verbatim,
			// replacing any masked content (e.g. pasting a full `+55 (61) 9…` international number).
			if (passthroughPrefix && rawPaste.trimStart().startsWith(passthroughPrefix)) {
				const text = rawPaste.trim();
				emitPassthrough(text, text.length);
				return;
			}

			let pasteText = rawPaste;
			if (normalizePaste) {
				try {
					pasteText = normalizePaste(rawPaste);
				} catch {
					pasteText = rawPaste;
				}
			}
			// When the mask uses separators, treat them as segment boundaries and zero-pad each
			// segment before filling — so an unpadded `1/1/2000` becomes `01/01/2000` instead of
			// packing left-to-right into `11/20/00`. Mirrors the typing-path padSegmentBeforeSeparator.
			if (separators?.length) {
				pasteText = padPastedSegments(pasteText, mask, separators);
			}
			const selStart = input.selectionStart ?? 0;
			const selEnd = input.selectionEnd ?? 0;
			const currentSlot = buildSlotString(value, mask, placeholderChar);
			const slots = currentSlot.split('');

			// If there's a range selection, clear those slots first
			if (selStart !== selEnd) {
				for (let i = selStart; i < Math.min(selEnd, mask.length); i++) {
					if (isMaskToken(mask[i])) {
						slots[i] = placeholderChar;
					}
				}
			}

			// Find starting token index
			let tokenIdx = firstTokenIndexAtOrAfter(mask, selStart);
			if (tokenIdx === -1) {
				tokenIdx = firstTokenIndexAtOrAfter(mask, 0);
			}
			if (tokenIdx === -1) {
				return;
			}

			let lastFilled = tokenIdx;
			let pasteCharIdx = 0;

			while (pasteCharIdx < pasteText.length && tokenIdx !== -1 && tokenIdx < mask.length) {
				const maskChar = mask[tokenIdx];
				if (maskChar === undefined) {
					break;
				}
				const pasteChar = pasteText[pasteCharIdx];
				if (pasteChar === undefined) {
					break;
				}
				if (matchesToken(pasteChar, maskChar)) {
					slots[tokenIdx] = pasteChar;
					lastFilled = tokenIdx;
					tokenIdx = nextTokenIndex(mask, tokenIdx);
					pasteCharIdx++;
				} else {
					// Skip non-matching paste chars (e.g. '/' in '10/10/2026' when pasting over slots)
					pasteCharIdx++;
				}
			}

			const newCaret = nextTokenIndex(mask, lastFilled);
			emit(slots.join(''), newCaret);
		},
		[value, mask, placeholderChar, separators, emit, emitPassthrough, passthroughPrefix, normalizePaste]
	);

	/**
	 * Fallback onChange for input methods that bypass keydown (autofill, dictation, etc.).
	 * Applies normalizePaste first (so an autofilled `+1…` number is normalized like a paste),
	 * then uses the compacting path: extract raw → fill slots left-to-right → emit.
	 * Caret goes to end since we can't determine intent.
	 */
	const handleChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			let inputValue = e.target.value;
			if (normalizePaste) {
				try {
					inputValue = normalizePaste(inputValue);
				} catch {
					inputValue = e.target.value;
				}
			}
			const slotString = buildSlotString(inputValue, mask, placeholderChar);
			onValueChange(buildChange(slotString));
		},
		[mask, placeholderChar, buildChange, onValueChange, normalizePaste]
	);

	// Passthrough mode: render a plain text input — no mask, no maxLength cap (international numbers
	// exceed the mask length), validation deferred to the consumer. Both modes render the same
	// <Input> element, so flipping in/out of passthrough updates props without a remount and the
	// caret/focus survive the transition.
	if (isPassthrough) {
		return (
			<Input
				autoComplete='off'
				{...restProps}
				ref={mergedRef}
				value={value}
				onChange={e => {
					const next = e.target.value;
					onValueChange({ raw: next, formatted: next, isComplete: next.length > 0, isValid: next.length > 0 });
				}}
			/>
		);
	}

	const currentSlot = buildSlotString(value, mask, placeholderChar);
	const change = buildChange(currentSlot);
	const showInvalid = change.isComplete && !change.isValid;

	// The displayed value: use slotStringToValue so empty fields show '' (HTML placeholder visible)
	const displayValue = slotStringToValue(currentSlot, mask, placeholderChar);

	return (
		<Input
			autoComplete='off'
			{...restProps}
			ref={mergedRef}
			value={displayValue}
			maxLength={mask.length}
			aria-invalid={showInvalid ? true : undefined}
			onKeyDown={handleKeyDown}
			onPaste={handlePaste}
			onChange={handleChange}
		/>
	);
});
